import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Row, Col, Card, Breadcrumb, Tab, Tabs, ListGroup, Badge } from 'react-bootstrap';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import GifLoder from '../../components/GifLoder';
import CustomToast from '../../components/CustomToast';

const emptyItem = (index = 0) => ({
    title: '',
    description: '',
    list_name: 'To Do',
    priority: 'Low',
    estimated_hours: null,
    due_offset_days: null,
    sort_order: index,
    assign_to_lead: true,
});

const emptyTemplate = (formType = '') => ({
    template_name: '',
    notes: '',
    form_type: formType,
    is_active: true,
    items: [emptyItem(0)],
});

const ClientFormTaskTemplates = () => {
    const { accessToken } = useAuth();
    const navigate = useNavigate();
    const toastRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formTypes, setFormTypes] = useState([]);
    const [listNames, setListNames] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [activeType, setActiveType] = useState(null);

    // templates keyed by form_type
    const [templatesByType, setTemplatesByType] = useState({});

    // editor state
    const [editorVisible, setEditorVisible] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState(null);
    const [editorTemplate, setEditorTemplate] = useState(null);
    const [loadingEditor, setLoadingEditor] = useState(false);

    const authHeaders = { Authorization: `Bearer ${accessToken}` };

    const showMessage = (severity, detail) => {
        const summary = severity.charAt(0).toUpperCase() + severity.slice(1);
        toastRef.current?.show(severity, summary, detail);
    };

    const fetchMeta = async () => {
        const res = await axios.get(`${config.apiBASEURL}/ClientFormTaskTemplateRoutes/meta`, { headers: authHeaders });
        return res.data;
    };

    const fetchTemplatesForType = async (formType) => {
        const res = await axios.get(
            `${config.apiBASEURL}/ClientFormTaskTemplateRoutes/templates?form_type=${encodeURIComponent(formType)}`,
            { headers: authHeaders }
        );
        return res.data.templates || [];
    };

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const meta = await fetchMeta();
                const types = meta.form_types || [];
                setFormTypes(types);
                setListNames(meta.list_names || []);
                setPriorities(meta.priorities || []);
                if (types.length) {
                    setActiveType(types[0].type);
                    // Fetch all types at once so counts are correct from the start
                    const results = await Promise.all(
                        types.map((cat) => fetchTemplatesForType(cat.type))
                    );
                    const byType = {};
                    types.forEach((cat, i) => { byType[cat.type] = results[i]; });
                    setTemplatesByType(byType);
                }
            } catch (err) {
                console.error(err);
                showMessage('error', 'Failed to load template data.');
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [accessToken]);

    const handleTabSelect = (formType) => {
        setActiveType(formType);
    };

    const openNewEditor = () => {
        setEditingTemplateId(null);
        setEditorTemplate(emptyTemplate(activeType));
        setEditorVisible(true);
    };

    const openEditEditor = async (templateId) => {
        setLoadingEditor(true);
        setEditorVisible(true);
        setEditingTemplateId(templateId);
        try {
            const res = await axios.get(
                `${config.apiBASEURL}/ClientFormTaskTemplateRoutes/templates/${templateId}`,
                { headers: authHeaders }
            );
            const t = res.data.template;
            setEditorTemplate({
                template_name: t.template_name || '',
                notes: t.notes || '',
                form_type: t.form_type,
                is_active: t.is_active !== false,
                items: t.items?.length
                    ? t.items.map((item, i) => ({
                        title: item.title || '',
                        description: item.description || '',
                        list_name: item.list_name || 'To Do',
                        priority: item.priority || 'Low',
                        estimated_hours: item.estimated_hours != null ? Number(item.estimated_hours) : null,
                        due_offset_days: item.due_offset_days,
                        sort_order: item.sort_order ?? i,
                        assign_to_lead: item.assign_to_lead !== false,
                    }))
                    : [emptyItem(0)],
            });
        } catch (err) {
            showMessage('error', 'Failed to load template.');
            setEditorVisible(false);
        } finally {
            setLoadingEditor(false);
        }
    };

    const handleSetDefault = async (templateId) => {
        try {
            await axios.patch(
                `${config.apiBASEURL}/ClientFormTaskTemplateRoutes/templates/${templateId}/set-default`,
                {},
                { headers: authHeaders }
            );
            const templates = await fetchTemplatesForType(activeType);
            setTemplatesByType((prev) => ({ ...prev, [activeType]: templates }));
        } catch (err) {
            showMessage('error', 'Failed to update default template.');
        }
    };

    const handleDeleteTemplate = async (e, templateId) => {
        confirmPopup({
            target: e.currentTarget,
            message: 'Delete this template? This cannot be undone.',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(
                        `${config.apiBASEURL}/ClientFormTaskTemplateRoutes/templates/${templateId}`,
                        { headers: authHeaders }
                    );
                    showMessage('success', 'Template deleted.');
                    const templates = await fetchTemplatesForType(activeType);
                    setTemplatesByType((prev) => ({ ...prev, [activeType]: templates }));
                } catch (err) {
                    showMessage('error', 'Failed to delete template.');
                }
            },
        });
    };

    const updateItem = (index, field, value) => {
        setEditorTemplate((prev) => ({
            ...prev,
            items: prev.items.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
        }));
    };

    const addItem = () => {
        setEditorTemplate((prev) => ({
            ...prev,
            items: [...prev.items, emptyItem(prev.items.length)],
        }));
    };

    const removeItem = (index) => {
        setEditorTemplate((prev) => ({
            ...prev,
            items: prev.items.length <= 1 ? [emptyItem(0)] : prev.items.filter((_, i) => i !== index),
        }));
    };

    const moveItem = (index, direction) => {
        setEditorTemplate((prev) => {
            const next = [...prev.items];
            const target = index + direction;
            if (target < 0 || target >= next.length) return prev;
            [next[index], next[target]] = [next[target], next[index]];
            return { ...prev, items: next.map((row, i) => ({ ...row, sort_order: i })) };
        });
    };

    const handleSave = async () => {
        if (!editorTemplate?.template_name?.trim()) {
            showMessage('warn', 'Template name is required.');
            return;
        }

        const validItems = editorTemplate.items
            .map((item, index) => ({ ...item, sort_order: index }))
            .filter((item) => String(item.title || '').trim());

        if (validItems.length === 0) {
            showMessage('warn', 'Add at least one task with a title.');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                template_name: editorTemplate.template_name.trim(),
                notes: editorTemplate.notes,
                is_active: editorTemplate.is_active,
                items: validItems,
            };

            if (editingTemplateId) {
                await axios.put(
                    `${config.apiBASEURL}/ClientFormTaskTemplateRoutes/templates/${editingTemplateId}`,
                    payload,
                    { headers: authHeaders }
                );
                showMessage('success', 'Template updated.');
            } else {
                await axios.post(
                    `${config.apiBASEURL}/ClientFormTaskTemplateRoutes/templates`,
                    { ...payload, form_type: editorTemplate.form_type },
                    { headers: authHeaders }
                );
                showMessage('success', 'Template created.');
            }

            setEditorVisible(false);
            const templates = await fetchTemplatesForType(activeType);
            setTemplatesByType((prev) => ({ ...prev, [activeType]: templates }));
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to save template.';
            showMessage('error', msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <GifLoder />;

    return (
        <Row className="body_content">
            <ConfirmPopup />
            <CustomToast ref={toastRef} />

            <Col className="mx-0">
                <Row className="mx-0 mb-4 align-items-center">
                    <Col>
                        <Breadcrumb>
                            <Breadcrumb.Item onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
                                <i className="pi pi-angle-left" /> Back
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>Form Task Templates</Breadcrumb.Item>
                        </Breadcrumb>
                        <p className="text-muted mb-0 mt-1" style={{ fontSize: '13px' }}>
                            Define reusable task lists per form type. When approving a submission, pick a template and the tasks are added to the new board automatically.
                        </p>
                    </Col>
                </Row>

                <Card>
                    <Card.Body>
                        <Tabs
                            activeKey={activeType}
                            onSelect={handleTabSelect}
                            className="mb-3"
                        >
                            {formTypes.map((cat) => {
                                const templates = templatesByType[cat.type] || [];
                                return (
                                    <Tab
                                        key={cat.type}
                                        eventKey={cat.type}
                                        title={`${cat.type} (${templates.length})`}
                                    >
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <span className="text-muted" style={{ fontSize: '13px' }}>
                                                {cat.description || cat.category_name}
                                            </span>
                                            <Button
                                                label="New template"
                                                icon="pi pi-plus"
                                                severity="secondary"
                                                onClick={openNewEditor}
                                            />
                                        </div>

                                        {templates.length === 0 ? (
                                            <p className="text-muted">No templates yet for {cat.type}. Click "New template" to create one.</p>
                                        ) : (
                                            <ListGroup>
                                                {templates.map((t) => (
                                                    <ListGroup.Item
                                                        key={t.template_id}
                                                        className="d-flex justify-content-between align-items-start py-3"
                                                        style={t.is_default ? { borderLeft: '4px solid #198754' } : {}}
                                                    >
                                                        <div>
                                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                <strong>{t.template_name}</strong>
                                                                {t.is_default && (
                                                                    <Badge bg="success" style={{ fontSize: '11px' }}>Default</Badge>
                                                                )}
                                                                {!t.is_active && (
                                                                    <Badge bg="secondary" style={{ fontSize: '11px' }}>Inactive</Badge>
                                                                )}
                                                            </div>
                                                            {t.notes && (
                                                                <div className="text-muted mt-1" style={{ fontSize: '12px' }}>{t.notes}</div>
                                                            )}
                                                            <div className="mt-1">
                                                                <span className="badge bg-primary me-1">{t.task_count} task{t.task_count !== 1 ? 's' : ''}</span>
                                                            </div>
                                                        </div>
                                                        <div className="d-flex gap-1 align-items-center">
                                                            <Button
                                                                icon={t.is_default ? 'pi pi-star-fill' : 'pi pi-star'}
                                                                severity={t.is_default ? 'success' : 'secondary'}
                                                                text
                                                                tooltip={t.is_default ? 'Remove default' : 'Set as default'}
                                                                tooltipOptions={{ position: 'top' }}
                                                                onClick={() => handleSetDefault(t.template_id)}
                                                            />
                                                            <Button
                                                                icon="pi pi-pencil"
                                                                severity="info"
                                                                text
                                                                tooltip="Edit"
                                                                tooltipOptions={{ position: 'top' }}
                                                                onClick={() => openEditEditor(t.template_id)}
                                                            />
                                                            <Button
                                                                icon="pi pi-trash"
                                                                severity="danger"
                                                                text
                                                                tooltip="Delete"
                                                                tooltipOptions={{ position: 'top' }}
                                                                onClick={(e) => handleDeleteTemplate(e, t.template_id)}
                                                            />
                                                        </div>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        )}
                                    </Tab>
                                );
                            })}
                        </Tabs>
                    </Card.Body>
                </Card>
            </Col>

            {/* Editor Dialog */}
            <Dialog
                header={editingTemplateId ? 'Edit Template' : 'New Template'}
                visible={editorVisible}
                onHide={() => setEditorVisible(false)}
                style={{ width: '720px' }}
                maximizable
                footer={
                    <div className="d-flex justify-content-end gap-2">
                        <Button label="Cancel" severity="secondary" onClick={() => setEditorVisible(false)} />
                        <Button label="Save template" icon="pi pi-save" onClick={handleSave} loading={saving} />
                    </div>
                }
            >
                {loadingEditor ? (
                    <GifLoder />
                ) : editorTemplate && (
                    <>
                        <Row className="g-3 mb-3">
                            <Col md={8}>
                                <label className="form-label">Template name *</label>
                                <InputText
                                    value={editorTemplate.template_name}
                                    onChange={(e) => setEditorTemplate((p) => ({ ...p, template_name: e.target.value }))}
                                    className="w-100"
                                    placeholder="e.g. Standard Wedding Onboarding"
                                />
                            </Col>
                            <Col md={4} className="d-flex align-items-end">
                                <div className="d-flex align-items-center gap-2">
                                    <Checkbox
                                        inputId="is_active"
                                        checked={editorTemplate.is_active}
                                        onChange={(e) => setEditorTemplate((p) => ({ ...p, is_active: e.checked }))}
                                    />
                                    <label htmlFor="is_active">Active</label>
                                </div>
                            </Col>
                            <Col md={12}>
                                <label className="form-label">Notes (optional)</label>
                                <InputTextarea
                                    value={editorTemplate.notes}
                                    onChange={(e) => setEditorTemplate((p) => ({ ...p, notes: e.target.value }))}
                                    rows={2}
                                    className="w-100"
                                />
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>Tasks</strong>
                            <Button label="Add task" icon="pi pi-plus" severity="secondary" size="small" onClick={addItem} />
                        </div>

                        {editorTemplate.items.map((item, index) => (
                            <Card key={index} className="mb-2 border">
                                <Card.Body className="py-2">
                                    <Row className="g-2">
                                        <Col md={6}>
                                            <label className="form-label mb-1" style={{ fontSize: '12px' }}>Title *</label>
                                            <InputText
                                                value={item.title}
                                                onChange={(e) => updateItem(index, 'title', e.target.value)}
                                                className="w-100"
                                                placeholder="Task title"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <label className="form-label mb-1" style={{ fontSize: '12px' }}>Column</label>
                                            <Dropdown
                                                value={item.list_name}
                                                options={listNames.map((n) => ({ label: n, value: n }))}
                                                onChange={(e) => updateItem(index, 'list_name', e.value)}
                                                className="w-100"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <label className="form-label mb-1" style={{ fontSize: '12px' }}>Priority</label>
                                            <Dropdown
                                                value={item.priority}
                                                options={priorities.map((p) => ({ label: p, value: p }))}
                                                onChange={(e) => updateItem(index, 'priority', e.value)}
                                                className="w-100"
                                            />
                                        </Col>
                                        <Col md={12}>
                                            <label className="form-label mb-1" style={{ fontSize: '12px' }}>Description</label>
                                            <InputTextarea
                                                value={item.description}
                                                onChange={(e) => updateItem(index, 'description', e.target.value)}
                                                rows={2}
                                                className="w-100"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <label className="form-label mb-1" style={{ fontSize: '12px' }}>Est. hours</label>
                                            <InputNumber
                                                value={item.estimated_hours}
                                                onValueChange={(e) => updateItem(index, 'estimated_hours', e.value)}
                                                min={0}
                                                className="w-100"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <label className="form-label mb-1" style={{ fontSize: '12px' }}>Due in (days)</label>
                                            <InputNumber
                                                value={item.due_offset_days}
                                                onValueChange={(e) => updateItem(index, 'due_offset_days', e.value)}
                                                min={0}
                                                className="w-100"
                                            />
                                        </Col>
                                        <Col md={4} className="d-flex align-items-end">
                                            <div className="d-flex align-items-center gap-2">
                                                <Checkbox
                                                    inputId={`lead-${index}`}
                                                    checked={item.assign_to_lead}
                                                    onChange={(e) => updateItem(index, 'assign_to_lead', e.checked)}
                                                />
                                                <label htmlFor={`lead-${index}`} style={{ fontSize: '12px' }}>Assign to lead</label>
                                            </div>
                                        </Col>
                                        <Col md={2} className="d-flex align-items-end justify-content-end gap-1">
                                            <Button icon="pi pi-arrow-up" severity="secondary" text onClick={() => moveItem(index, -1)} disabled={index === 0} />
                                            <Button icon="pi pi-arrow-down" severity="secondary" text onClick={() => moveItem(index, 1)} disabled={index === editorTemplate.items.length - 1} />
                                            <Button icon="pi pi-trash" severity="danger" text onClick={() => removeItem(index)} />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        ))}
                    </>
                )}
            </Dialog>
        </Row>
    );
};

export default ClientFormTaskTemplates;
